import { useEffect, useCallback, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function usePushNotifications() {
    const { user } = useUser();
    const [permission, setPermission] = useState<NotificationPermission>(
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );
    const [subscribed, setSubscribed] = useState(false);

    const subscribe = useCallback(async () => {
        if (!user?.id || !VAPID_PUBLIC_KEY) return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            if (result !== 'granted') return;

            const registration = await navigator.serviceWorker.ready;
            let subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
                });
            }

            const sub = subscription.toJSON();
            if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys?.auth) return;

            // Save to DB via Supabase
            const { data: profile } = await supabase
                .from('user_profiles')
                .select('id')
                .eq('clerk_user_id', user.id)
                .single();

            if (!profile) return;

            await supabase
                .from('push_subscriptions')
                .upsert({
                    user_profile_id: profile.id,
                    endpoint: sub.endpoint,
                    p256dh: sub.keys.p256dh,
                    auth: sub.keys.auth,
                    updated_at: new Date().toISOString(),
                }, { onConflict: 'user_profile_id,endpoint' });

            setSubscribed(true);
        } catch (err) {
            // Silently fail — push is optional
        }
    }, [user?.id]);

    // Auto-subscribe on mount if permission already granted
    useEffect(() => {
        if (permission === 'granted' && !subscribed) {
            subscribe();
        }
    }, [permission, subscribed, subscribe]);

    return { permission, subscribed, subscribe };
}
